import React from "react";
import moment from "moment";
import { creatorStudioName, effectiveStudioOwner } from "../productOwnership";
import { ProductAction, ProductActionKey, formatRupee, productActionGroups } from "./productListRow";
import { NfcTagButton, ProductRowMenu } from "./ProductActions";
import { TagSave, shortUid } from "./NfcTagForm";
import { CompletenessDots, IconButton, ProductBadge, ProductThumb, StockSwitch } from "./ProductListBits";
import { ProductSort } from "./productListFilters";
import { PickableColumn, TableColumnField, extraTableColumns } from "./productTableColumns";
import { colourSwatch } from "./colourSwatches";
import PriceInput from "./PriceInput";
import { PlIcon } from "./plIcons";
import NeedsPhotoBadge from "../photoUpdate/NeedsPhotoBadge";

export type ItemHandlers = {
  draftRevisionsEnabled: boolean;
  authUser: any;
  onOpen: (product: any) => void;
  /** Opens the actions sheet; "price" lands in the Selling field ("+ Add price"). */
  onMore: (product: any, focus?: "price") => void;
  onStockToggle: (product: any, inStock: boolean) => void;
  onPreview: (images: any) => void;
  onAction: (key: ProductActionKey, product: any) => void;
  onPriceSave: (product: any, sellPrice: string, costPrice: string) => void;
  onTagSave: TagSave;
  highlightId?: string;
};

/** Studio SN is what people say out loud ("#105"); the long product id stays in the URL and actions. */
export const studioSn = (product: any): string =>
  product?.orgSrNo != null && product.orgSrNo !== "" ? `#${product.orgSrNo}` : product?.productUniqueId ? `#${product.productUniqueId}` : "";

const metaLine = (product: any, withCategory: boolean) =>
  [studioSn(product), withCategory ? product?.category?.name : "", product?.color].filter(Boolean).join(" · ");

/** Selling and cost price, each labelled, so nobody has to guess which one the list shows. */
const PriceLine = ({ product, onMore }: { product: any; onMore: (product: any, focus?: "price") => void }) => {
  const sell = formatRupee(product?.sellPrice);
  const cost = formatRupee(product?.costPrice);
  return sell || cost ? (
    <span className="pl-price">
      {sell ? (
        <span className="pl-price__item">
          <abbr title="Selling price">SP</abbr>
          {sell}
        </span>
      ) : null}
      {cost ? (
        <span className="pl-price__item is-cost">
          <abbr title="Cost price">CP</abbr>
          {cost}
        </span>
      ) : null}
    </span>
  ) : (
    <button
      type="button"
      className="pl-price is-none"
      onClick={(event) => {
        event.stopPropagation();
        onMore(product, "price");
      }}
    >
      + Add price
    </button>
  );
};

/** "NFC" chip when a tag is bound; the UID shows on hover and in the ⋯ sheet a tap opens. Compact sits on the grid photo. */
const NfcChip = ({ product, onMore, compact = false }: { product: any; onMore: (product: any) => void; compact?: boolean }) => {
  const uid = String(product?.xuid ?? "").trim();
  return uid ? (
    <button
      type="button"
      className={`pl-nfc-chip${compact ? " is-over" : ""}`}
      title={`NFC bound · ${uid}`}
      aria-label={`NFC bound, UID ${uid}`}
      onClick={(event) => {
        event.stopPropagation();
        onMore(product);
      }}
    >
      <PlIcon name="tag" size={13} />
      NFC
      {compact ? null : <span className="pl-nfc-chip__uid">{shortUid(uid)}</span>}
    </button>
  ) : null;
};

const MoreButton = ({ product, h, className = "" }: { product: any; h: ItemHandlers; className?: string }) => (
  <button
    type="button"
    className={`pl-iconbtn is-plain is-more ${className}`}
    aria-label={`Actions for ${product?.name || "product"}`}
    onClick={(event) => {
      event.stopPropagation();
      h.onMore(product);
    }}
  >
    <PlIcon name="more" />
  </button>
);

/** Phone card (P01): 68×110 photo, name + ⋯, #SN · category · colour, price, badge, dots, stock. */
export const ProductCard = ({ product, h }: { product: any; h: ItemHandlers }) => (
  <article data-pid={product?._id} className={`pl-card${h.highlightId && h.highlightId === product?.productUniqueId ? " is-highlight" : ""}`}>
    <ProductThumb product={product} className="is-card" onPreview={h.onPreview} />
    <div className="pl-card__info">
      <div className="pl-card__top">
        <button type="button" className="pl-card__name" onClick={() => h.onOpen(product)}>
          {product?.name || "Untitled product"}
        </button>
        <MoreButton product={product} h={h} />
      </div>
      <div className="pl-card__meta pl-ellipsis">{metaLine(product, true)}</div>
      <div className="pl-card__mid">
        <PriceLine product={product} onMore={h.onMore} />
        <NfcChip product={product} onMore={h.onMore} />
        <NeedsPhotoBadge status={product?.photoStatus} />
      </div>
      <div className="pl-card__bottom">
        <ProductBadge product={product} draftRevisionsEnabled={h.draftRevisionsEnabled} />
        <CompletenessDots product={product} />
        <span className="pl-grow" />
        <StockSwitch product={product} onToggle={h.onStockToggle} />
      </div>
    </div>
  </article>
);

/** Tablet grid card (T01): tall photo, name, #SN · colour, price + badge, dots + stock + ⋯. */
export const ProductGridCard = ({ product, h }: { product: any; h: ItemHandlers }) => (
  <article data-pid={product?._id} className={`pl-gcard${h.highlightId && h.highlightId === product?.productUniqueId ? " is-highlight" : ""}`}>
    <ProductThumb product={product} className="is-grid" preset="PRODUCT_CARD" onPreview={h.onPreview} />
    <NfcChip product={product} onMore={h.onMore} compact />
    <NeedsPhotoBadge status={product?.photoStatus} over />
    <div className="pl-gcard__info">
      <button type="button" className="pl-gcard__name pl-ellipsis" onClick={() => h.onOpen(product)} title={product?.name}>
        {product?.name || "Untitled product"}
      </button>
      <div className="pl-gcard__meta pl-ellipsis">{metaLine(product, false)}</div>
      <div className="pl-row">
        <span className="pl-grow">
          <PriceLine product={product} onMore={h.onMore} />
        </span>
        <ProductBadge product={product} draftRevisionsEnabled={h.draftRevisionsEnabled} />
      </div>
      <div className="pl-row">
        <CompletenessDots product={product} />
        <span className="pl-grow" />
        <StockSwitch product={product} onToggle={h.onStockToggle} />
        <MoreButton product={product} h={h} className="is-sm" />
      </div>
    </div>
  </article>
);

const createdText = (product: any) =>
  product?.createdAt ? moment(new Date(product.createdAt)).subtract(5, "hours").subtract(30, "minutes").format("D MMM YYYY") : "";

const inlineAction = (product: any, h: ItemHandlers, key: ProductActionKey): ProductAction | undefined =>
  productActionGroups(product, { authUser: h.authUser, draftRevisionsEnabled: h.draftRevisionsEnabled })
    .flatMap((g) => g.actions)
    .find((a) => a.key === key);

const TextCell = ({ value, sub }: { value: unknown; sub?: unknown }) => {
  const text = String(value ?? "").trim();
  const subText = String(sub ?? "").trim();
  return (
    <div className="pl-tcell" title={[text, subText].filter(Boolean).join(" · ") || undefined}>
      <span className="pl-ellipsis">{text || <span className="pl-dim">—</span>}</span>
      {subText ? <span className="pl-meta pl-ellipsis">{subText}</span> : null}
    </div>
  );
};

const extraCell = (field: TableColumnField, product: any, h: ItemHandlers): React.ReactNode => {
  switch (field) {
    case "color": {
      const swatch = colourSwatch(product?.color);
      return product?.color ? (
        <span className="pl-colour" title={product.color}>
          <i className="pl-cdot" style={{ background: swatch }} aria-hidden="true" />
          <span className="pl-ellipsis">{product.color}</span>
        </span>
      ) : (
        <span className="pl-dim">—</span>
      );
    }
    case "costPrice":
      return (
        <PriceInput
          value={product?.costPrice}
          label={`Cost price of ${product?.name || "product"}`}
          emptyText="+ Add cost"
          onCommit={(next) => h.onPriceSave(product, String(product?.sellPrice ?? ""), next)}
        />
      );
    case "srNo":
      return <TextCell value={product?.srNo} />;
    case "nfcTag":
      return <TextCell value={product?.xuid} />;
    case "owners": {
      const creator = creatorStudioName(product);
      const owner = effectiveStudioOwner(product?.productOwner, creator);
      const design = effectiveStudioOwner(product?.designOwner, creator);
      return <TextCell value={owner} sub={design && design !== owner ? `Design: ${design}` : ""} />;
    }
    case "orgName":
      return <TextCell value={creatorStudioName(product)} />;
    case "createdAt":
      return <TextCell value={createdText(product)} />;
    default:
      return null;
  }
};

const SortHeader = ({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey?: string;
  sort: ProductSort;
  onSort: (sort: ProductSort) => void;
}) => {
  if (!sortKey) return <span role="columnheader">{label}</span>;
  const active = sort.name === sortKey && sort.order;
  const next: ProductSort = active && sort.order === "asc" ? { name: sortKey, order: "desc" } : { name: sortKey, order: "asc" };
  return (
    <span role="columnheader" aria-sort={active ? (sort.order === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className={`pl-th-sort${active ? " is-on" : ""}`} onClick={() => onSort(next)}>
        {label}
        {active ? <span aria-hidden="true">{sort.order === "asc" ? "↑" : "↓"}</span> : null}
      </button>
    </span>
  );
};

const subLine = (product: any, shown: TableColumnField[]) =>
  [
    shown.includes("category") ? product?.category?.name : "",
    shown.includes("designNo") && product?.designNum ? `Design no. ${product.designNum}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

/** Laptop table (D01): SR | Product | Colour | Selling price | Stock | Status | tag · QR · ⋯. */
export const ProductTable = ({
  products,
  shown,
  sort,
  onSort,
  h,
  busy,
}: {
  products: any[];
  shown: TableColumnField[];
  sort: ProductSort;
  onSort: (sort: ProductSort) => void;
  h: ItemHandlers;
  busy: boolean;
}) => {
  const { beforePrice, cost } = extraTableColumns(shown);
  const template = [
    "44px",
    "minmax(220px, 3fr)",
    ...beforePrice.map((c) => c.width),
    "124px",
    ...(cost ? [cost.width] : []),
    "60px",
    "110px",
    "120px",
  ].join(" ");
  const extraHead = (c: PickableColumn) => (
    <SortHeader key={c.field} label={c.header || c.label} sortKey={c.sortKey} sort={sort} onSort={onSort} />
  );
  return (
    <div className={`pl-table${busy ? " is-refreshing" : ""}`} role="table" aria-label="Products" aria-busy={busy || undefined}>
      <div className="pl-tr is-head" role="row" style={{ gridTemplateColumns: template }}>
        <SortHeader label="Sr" sortKey="orgSrNo" sort={sort} onSort={onSort} />
        <SortHeader label="Product" sortKey="name" sort={sort} onSort={onSort} />
        {beforePrice.map(extraHead)}
        <span role="columnheader">Selling price</span>
        {cost ? <span role="columnheader">{cost.header}</span> : null}
        <span role="columnheader">Stock</span>
        <span role="columnheader">Status</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {products.map((product) => {
        const tag = inlineAction(product, h, "tag");
        const qr = inlineAction(product, h, "add-qr");
        const sub = subLine(product, shown);
        return (
          <div
            key={product?._id || product?.productUniqueId}
            data-pid={product?._id}
            className={`pl-tr${h.highlightId && h.highlightId === product?.productUniqueId ? " is-highlight" : ""}`}
            role="row"
            style={{ gridTemplateColumns: template }}
          >
            <div role="cell" className="pl-td is-sr">
              {product?.orgSrNo ?? ""}
            </div>
            <div role="cell" className="pl-td">
              <div className="pl-pcell">
                <ProductThumb product={product} className="is-row" onPreview={h.onPreview} />
                <div className="pl-pcell__text">
                  <button type="button" className="pl-pcell__name" onClick={() => h.onOpen(product)} title={product?.name}>
                    {product?.name || "Untitled product"}
                  </button>
                  {sub ? <div className="pl-pcell__sub pl-ellipsis">{sub}</div> : null}
                </div>
              </div>
            </div>
            {beforePrice.map((c) => (
              <div key={c.field} role="cell" className={`pl-td is-${c.field}`}>
                {extraCell(c.field, product, h)}
              </div>
            ))}
            <div role="cell" className="pl-td">
              <PriceInput
                value={product?.sellPrice}
                label={`Selling price of ${product?.name || "product"}`}
                onCommit={(next) => h.onPriceSave(product, next, String(product?.costPrice ?? ""))}
              />
            </div>
            {cost ? (
              <div role="cell" className="pl-td is-costPrice">
                {extraCell("costPrice", product, h)}
              </div>
            ) : null}
            <div role="cell" className="pl-td is-stock">
              <StockSwitch product={product} onToggle={h.onStockToggle} />
            </div>
            <div role="cell" className="pl-td">
              <div className="pl-status">
                <ProductBadge product={product} draftRevisionsEnabled={h.draftRevisionsEnabled} />
                <CompletenessDots product={product} />
              </div>
              <NeedsPhotoBadge status={product?.photoStatus} />
            </div>
            <div role="cell" className="pl-td">
              <div className="pl-row-actions">
                {tag ? (
                  <NfcTagButton product={product} label={tag.label} onSave={h.onTagSave} />
                ) : (
                  <span className="pl-iconbtn is-ghost" aria-hidden="true" />
                )}
                {qr ? (
                  <IconButton icon="qr" label={qr.label} onClick={() => h.onAction("add-qr", product)} />
                ) : (
                  <span className="pl-iconbtn is-ghost" aria-hidden="true" />
                )}
                <ProductRowMenu product={product} authUser={h.authUser} draftRevisionsEnabled={h.draftRevisionsEnabled} onAction={h.onAction} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
